import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, Form, Input, List, Modal, Popconfirm, Select, Space, Tag, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useMeasurementReport } from './measurementReport/MeasurementReportContext';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { useRoleControl } from '../../helpers/roleControl';
import { MeasurementSectionShell, buildMeasurementSectionMeta } from './measurementReport/MeasurementSectionShell';
import { formatUtcTimestamp } from '../../utils/measurementTime';

const { Text } = Typography;

interface CommonSectionProps {
    section: any;
}

const parseJsonText = (text: unknown): { valid: boolean; value: any } => {
    if (typeof text !== 'string' || !text.trim()) {
        return { valid: false, value: null };
    }
    try {
        return { valid: true, value: JSON.parse(text) };
    } catch {
        return { valid: false, value: null };
    }
};

const isSystemMetadata = (item: any) =>
    item?.textType === 'capture_metadata' || item?.textType === 'trigger_metadata';

const isJsonObject = (value: unknown): value is Record<string, any> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const getMarkedText = (value: unknown): string | null => {
    if (!isJsonObject(value) || value.format !== 'text' || typeof value.text !== 'string') {
        return null;
    }
    return value.text;
};

const serializeTextNote = (text: string) => JSON.stringify({
    format: 'text',
    text,
});

const shouldDisplayMeasurementText = (item: any, interpretTextAsJson?: boolean) => {
    const parsed = parseJsonText(item?.text);
    if (!parsed.valid) return true;
    if (getMarkedText(parsed.value) !== null) return true;
    if (typeof interpretTextAsJson === 'boolean') return interpretTextAsJson;
    return item?.textType === 'trigger_metadata' || item?.textType === 'capture_metadata';
};

const humanizeJsonKey = (key: string) => key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (character) => character.toUpperCase());

const JsonScalar: React.FC<{ value: unknown }> = ({ value }) => {
    if (value === null) return <Text type="secondary">null</Text>;
    if (typeof value === 'boolean') {
        return <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag>;
    }
    if (typeof value === 'number') return <Text code>{value}</Text>;
    if (typeof value === 'string') {
        return (
            <div className="report-measurement-json-markdown">
                <ReactMarkdown>{value}</ReactMarkdown>
            </div>
        );
    }
    return <Text>{String(value)}</Text>;
};

const JsonValue: React.FC<{ value: unknown; depth?: number }> = ({ value, depth = 0 }) => {
    if (Array.isArray(value)) {
        if (value.length === 0) return <Text type="secondary">Empty list</Text>;
        return (
            <div className="report-measurement-json-array">
                {value.map((entry, index) => (
                    <div className="report-measurement-json-array-item" key={index}>
                        <span className="report-measurement-json-index">{index + 1}</span>
                        <div className="report-measurement-json-array-value">
                            <JsonValue value={entry} depth={depth + 1} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (isJsonObject(value)) {
        const entries = Object.entries(value);
        if (entries.length === 0) return <Text type="secondary">Empty object</Text>;
        return (
            <div className={`report-measurement-json-object ${depth > 0 ? 'report-measurement-json-object--nested' : ''}`}>
                {entries.map(([key, entry]) => {
                    const isNested = Array.isArray(entry) || isJsonObject(entry);
                    return (
                        <div
                            className={`report-measurement-json-field ${isNested ? 'report-measurement-json-field--nested' : ''}`}
                            key={key}
                        >
                            <div className="report-measurement-json-key">{humanizeJsonKey(key)}</div>
                            <div className="report-measurement-json-value">
                                <JsonValue value={entry} depth={depth + 1} />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    return <JsonScalar value={value} />;
};

const FormattedJsonView: React.FC<{ value: unknown }> = ({ value }) => (
    <div className="report-measurement-json-document">
        <JsonValue value={value} />
    </div>
);

// The report editor decides whether ordinary JSON is displayed. Tagged text
// notes and legacy non-JSON notes are always rendered as readable Markdown.
const MeasurementTextView: React.FC<{ item: any; defaultInterpretAsJson?: boolean }> = ({
    item,
    defaultInterpretAsJson,
}) => {
    const parsed = parseJsonText(item.text);
    const markedText = parsed.valid ? getMarkedText(parsed.value) : null;
    const defaultsToJson = markedText === null && (
        typeof defaultInterpretAsJson === 'boolean'
            ? defaultInterpretAsJson
            : item.textType === 'trigger_metadata' || item.textType === 'capture_metadata'
    );
    const interpretAsJson = defaultsToJson && parsed.valid;

    const meta = item.meta || {};

    return (
        <div className="report-measurement-note-view">
            {interpretAsJson && parsed.valid ? <FormattedJsonView value={parsed.value} /> : null}
            {!interpretAsJson && markedText !== null ? (
                <div className="report-measurement-note-markdown">
                    <ReactMarkdown>{markedText}</ReactMarkdown>
                </div>
            ) : null}
            {!interpretAsJson && !parsed.valid ? (
                <div className="report-measurement-note-markdown">
                    <ReactMarkdown>{String(item.text || '')}</ReactMarkdown>
                </div>
            ) : null}
            {(meta.triggeredByEmail || meta.externalRef) && (
                <div style={{ marginTop: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {meta.triggeredByEmail ? `Triggered by ${meta.triggeredByEmail}` : ''}
                        {meta.triggeredByEmail && meta.externalRef ? ' · ' : ''}
                        {meta.externalRef ? `Ref: ${meta.externalRef}` : ''}
                    </Text>
                </div>
            )}
        </div>
    );
};

export const ReportMeasurementTexts: React.FC<CommonSectionProps> = ({ section }) => {
    const { measurementId, measurement, loading, error, texts } = useMeasurementReport();
    const canEditTexts = useRoleControl(['admin', 'user']);
    const [localTexts, setLocalTexts] = useState<any[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingText, setEditingText] = useState<any | null>(null);
    const [form] = Form.useForm();
    const sectionTitle = section?.title || 'Texts';
    const sectionMeta = buildMeasurementSectionMeta(measurement);

    useEffect(() => {
        if (!measurementId) {
            setLocalTexts([]);
            return;
        }
        const next = texts.length > 0 ? texts : (measurement?.texts || []);
        setLocalTexts(next);
    }, [measurementId, texts, measurement]);

    if (!measurementId) {
        return (
            <MeasurementSectionShell title={sectionTitle}>
                <Alert message="Select a measurement to view notes." type="info" showIcon />
            </MeasurementSectionShell>
        );
    }

    if (loading) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Text>Loading notes...</Text>
                </div>
            </MeasurementSectionShell>
        );
    }

    if (error || !measurement) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <Alert message={error || 'Measurement not found'} type="error" showIcon />
            </MeasurementSectionShell>
        );
    }

    const entries = localTexts.filter((item) => (
        shouldDisplayMeasurementText(item, section?.interpretTextAsJson)
    ));

    const openAdd = () => {
        setEditingText(null);
        form.setFieldsValue({ text: '', textType: 'comment' });
        setModalOpen(true);
    };

    const openEdit = (entry: any) => {
        const parsed = parseJsonText(entry.text);
        const markedText = parsed.valid ? getMarkedText(parsed.value) : null;
        setEditingText(entry);
        form.setFieldsValue({
            text: markedText ?? entry.text,
            textType: entry.textType || 'comment',
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        if (!canEditTexts) {
            message.warning('You do not have permission to edit notes.');
            return;
        }
        try {
            const values = await form.validateFields();
            setSaving(true);
            const payload = {
                text: serializeTextNote(values.text),
                textType: values.textType,
                processingStage: 'processed',
            };
            if (editingText) {
                const res = await axios.patch(
                    ENDPOINTS.MEASUREMENT_TEXT(measurementId!, editingText.id),
                    payload,
                    { withCredentials: true }
                );
                const updated = res.data?.data || res.data;
                setLocalTexts(prev => prev.map(t => (t.id === updated.id ? updated : t)));
                message.success('Note updated');
            } else {
                const res = await axios.post(
                    ENDPOINTS.MEASUREMENT_TEXTS(measurementId!),
                    payload,
                    { withCredentials: true }
                );
                const created = res.data?.data || res.data;
                setLocalTexts(prev => [created, ...prev]);
                message.success('Note added');
            }
            setModalOpen(false);
            setEditingText(null);
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            console.error('Failed to save note', error);
            message.error(error.response?.data?.message || 'Failed to save note');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (entry: any) => {
        if (!canEditTexts) {
            message.warning('You do not have permission to delete notes.');
            return;
        }
        try {
            await axios.delete(
                ENDPOINTS.MEASUREMENT_TEXT(measurementId!, entry.id),
                { withCredentials: true }
            );
            setLocalTexts(prev => prev.filter(t => t.id !== entry.id));
            message.success('Note deleted');
        } catch (error: any) {
            console.error('Failed to delete note', error);
            message.error(error.response?.data?.message || 'Failed to delete note');
        }
    };

    return (
        <>
            <MeasurementSectionShell
                title={sectionTitle}
                meta={sectionMeta}
                toolbar={
                    <div className="report-measurement-note-toolbar">
                        <span className="report-measurement-pill">
                            {entries.length} {entries.length === 1 ? 'note' : 'notes'}
                        </span>
                        {canEditTexts ? (
                            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>
                                Add note
                            </Button>
                        ) : null}
                    </div>
                }
            >
                {entries.length === 0 ? (
                    <div className="report-measurement-panel-state report-measurement-panel-state--compact">
                        <Empty
                            description={localTexts.length > 0 ? 'No text notes to display' : 'No notes or comments'}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    </div>
                ) : (
                    <List
                        size="small"
                        dataSource={entries}
                        className="report-measurement-note-list"
                        renderItem={(item: any) => {
                            const canModifyEntry = canEditTexts && !isSystemMetadata(item);
                            return (
                                <List.Item className="report-measurement-note-card">
                                    <div className="report-measurement-note-content">
                                        <div className="report-measurement-note-head">
                                            <Space wrap>
                                                <Tag color={item.textType === 'trigger_metadata' ? 'purple' : (item.textType === 'capture_metadata' ? 'cyan' : 'blue')}>
                                                    {(item.textType || 'comment').replace(/_/g, ' ')}
                                                </Tag>
                                                {item.createdAt ? (
                                                    <Text type="secondary">
                                                        {formatUtcTimestamp(item.createdAt)}
                                                    </Text>
                                                ) : null}
                                            </Space>
                                            {canModifyEntry ? (
                                                <Space size={4}>
                                                    <Button
                                                        key="edit"
                                                        size="small"
                                                        type="link"
                                                        icon={<EditOutlined />}
                                                        onClick={() => openEdit(item)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Popconfirm
                                                        key="delete"
                                                        title="Delete this note?"
                                                        okText="Delete"
                                                        okButtonProps={{ danger: true }}
                                                        onConfirm={() => handleDelete(item)}
                                                    >
                                                        <Button size="small" type="link" danger icon={<DeleteOutlined />}>
                                                            Delete
                                                        </Button>
                                                    </Popconfirm>
                                                </Space>
                                            ) : null}
                                        </div>
                                        <MeasurementTextView
                                            item={item}
                                            defaultInterpretAsJson={section?.interpretTextAsJson}
                                        />
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                )}
            </MeasurementSectionShell>
            <Modal
                title={editingText ? 'Edit Note' : 'Add Note'}
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setEditingText(null);
                    form.resetFields();
                }}
                onOk={handleSave}
                confirmLoading={saving}
                okText={editingText ? 'Save' : 'Add'}
                okButtonProps={{ disabled: !canEditTexts }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Type"
                        name="textType"
                        rules={[{ required: true, message: 'Select a type' }]}
                    >
                        <Select
                            options={[
                                { value: 'comment', label: 'Comment' },
                                { value: 'description', label: 'Description' },
                                { value: 'note', label: 'Note' },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Text (Markdown supported)"
                        name="text"
                        rules={[{ required: true, message: 'Enter note text' }]}
                    >
                        <Input.TextArea rows={6} placeholder="Enter notes, lists, links, or other Markdown text" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ReportMeasurementTexts;
